import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface AppNavigationState {
  activeTreeId?: string;
  canManageMembers: boolean;
  canManagePublicTree?: boolean;
}

interface AppNavigationContextType extends AppNavigationState {
  setNavigationState: (updates: Partial<AppNavigationState>) => void;
  clearNavigationState: () => void;
}

const defaultNavigationState: AppNavigationState = {
  activeTreeId: undefined,
  canManageMembers: false,
  canManagePublicTree: false,
};

const AppNavigationContext = createContext<AppNavigationContextType | undefined>(undefined);

export const AppNavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [navigationState, setNavigationStateValue] = useState<AppNavigationState>(defaultNavigationState);

  const setNavigationState = useCallback((updates: Partial<AppNavigationState>) => {
    setNavigationStateValue((currentState) => ({
      ...currentState,
      ...updates,
    }));
  }, []);

  const clearNavigationState = useCallback(() => {
    setNavigationStateValue(defaultNavigationState);
  }, []);

  const value = useMemo<AppNavigationContextType>(() => ({
    ...navigationState,
    setNavigationState,
    clearNavigationState,
  }), [clearNavigationState, navigationState, setNavigationState]);

  return (
    <AppNavigationContext.Provider value={value}>
      {children}
    </AppNavigationContext.Provider>
  );
};

export const useAppNavigation = () => {
  const context = useContext(AppNavigationContext);
  if (!context) {
    throw new Error('useAppNavigation must be used within AppNavigationProvider');
  }

  return context;
};
